import React, { Component } from 'react';
import { Container, Form } from 'react-bootstrap';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import SendIcon from '@mui/icons-material/Send';
import { Add, Remove } from '@mui/icons-material';
import axios from "axios";
import Select from '../../../Components/PCSelect';
import WeekRangeSelect from '../../../Components/WeekRangeSelect';
import Swal from "sweetalert2";
import { formatDate, formatDisplayWeek, sumMoney, getWeekDetails, getWeekDayList, money, approvalActions, hasAction, isSuperAdmin, formatAmount } from '../../../helper';
import PhoneClinicGrid from '../../../Components/PhoneClinicGrid';
import RowActions from '../../../Components/PhoneClinicGrid/RowActions';
import { CARD as PC_CARD, COL_HEAD, LABEL, AMOUNT_INPUT, DATE_INPUT, DESC_INPUT, DESC_CLASS, WRAP_SELECT, fitDescription, keepOneLine } from '../shared/txStyles';
import StatusChip from '../shared/StatusChip';
import BulkActionBar from '../../../Components/PhoneClinicGrid/BulkActionBar';
import { runTransition, APPROVAL_MODULES } from '../shared/approvalApi';

import AmountInput from '../../../Components/AmountInput';
import PageHeader from '../shared/PageHeader';
import withRouter from '../../../helper/withRouter';
import { recordOpened, recordClosed, recordParamChanged, recordNotFound } from '../../../helper/recordLinks';
import { ownLimit, limitOptions } from '../../../helper/identity';

const BASE_PATH = '/admin/shop-payments';   // + '/<PUBLIC_ID>' opens one record
/* Unified card = shared token + this page's layout extras. */
const CARD = { ...PC_CARD, overflow: 'hidden' };

class ShopPayments extends Component {
    constructor(props) {
        super(props);
        this.state = {
            addShopPayment: false,
            isEdit: false,

            ShopPaymentsList: [],
            shopPaymentDays: [],
            ShopList: [],
            AllShopData: [],
            PaymentWeek: "",
            ShopPayment: "",
            PrevWeek: "0",
            CWCash: "0",
            CWAccount: "0",
            NetAmount: "0",
            OtherShop: "0",
            // Inter-shop transfer context for the selected shop-week, from GetShopHistory (F3).
            Transfer: null,
            CWCard: "0",
            TotalAmount: "0",
            ShopAccountPayDetails: [],
            AccountList: [],
            groupedOptions: [],
            SP_ID: "",
            Expense: "0",
            SP_STATUS: "PENDING",
            // Only whether to RENDER the bulk actions. Every transition is re-checked
            // server-side against pc_role_rights from the signed token.
            canApprove: hasAction('/admin/shop-payments', 'APPROVE'),
            selectedIds: [],
            bulkBusy: false,

            PC_USR_ROLE_ID: localStorage.getItem('PC_USR_ROLE_ID'),
            PC_GRP_ID: localStorage.getItem('PC_GRP_ID'),
            PC_SHOP_ID: localStorage.getItem('PC_GRP_ATT'),

            isShopDisable: false,
            AllWareHouseList: [],

            // Account-payment "From" entries where this shop is the source (Case 1). Display-only.
            FromEntries: [],
            FromEntriesTotal: 0
        };
        this.gridRef = React.createRef(null);
        this.openPid = null;   // public id of the record the form shows (helper/recordLinks.js)
    }

    /* ── Per-record links: /admin/shop-payments/<PUBLIC_ID> ── */
    componentDidMount() {
        const pid = this.props.params?.publicId;
        if (pid) this.openByPublicId(pid);
    }

    componentDidUpdate(prevProps) {
        recordParamChanged(this, prevProps, (pid) => this.openByPublicId(pid), () => this.handleExit());
    }

    // The record behind a link, from the same list endpoint with the server applying the signed-in
    // user's scope — then the normal edit form, exactly as the list's Edit button opens it.
    async openByPublicId(pid) {
        this.openPid = pid;
        this.props.toggleLoader(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Finance/PCGetAllShopPayment.php`, {
                PC_USR_ROLE_ID: this.state.PC_USR_ROLE_ID, PC_GRP_ID: this.state.PC_GRP_ID,
                PC_SHOP_ID: this.state.PC_SHOP_ID, PUBLIC_ID: pid,
            });
            const row = res.data?.success === 1 ? res.data.data[0] : null;
            this.props.toggleLoader(false);
            if (!row) return recordNotFound(this, BASE_PATH);
            await this.handleUpdateSP(row.SP_ID);
        } catch (error) {
            this.props.toggleLoader(false);
            console.error('openByPublicId error:', error);
            recordNotFound(this, BASE_PATH);
        }
    }

    // The list's Edit button: same form as before, and the record's link in the address bar.
    openFromList(item) {
        recordOpened(this, BASE_PATH, item.PUBLIC_ID);
        this.handleUpdateSP(item.SP_ID);
    }

    async getShopPayment(searchData = [], pagination = { recordOffset: 1, pageSize: 50 }) {
        this.props.toggleLoader(true);
        const formData = {
            PC_USR_ROLE_ID: this.state.PC_USR_ROLE_ID,
            PC_GRP_ID: this.state.PC_GRP_ID,
            PC_SHOP_ID: this.state.PC_SHOP_ID,
            searchData,
            pageSize: pagination.pageSize,
            recordOffset: pagination.recordOffset
        };
        const getSP = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Finance/PCGetAllShopPayment.php`, formData);
        const ShopPaymentData = getSP.data;
        let ShopPaymentsList = [];
        if (ShopPaymentData.success === 1) {
            ShopPaymentsList = ShopPaymentData.data;
            pagination.totalItems = parseInt(ShopPaymentData.Page_count[0]['TOTAL_PAGE_COUNT']);
            pagination.totalPages = Math.ceil(pagination.totalItems / pagination.pageSize || 50);
            this.gridRef.current?.setPaginationData(pagination);
        }
        this.setState({ ShopPaymentsList });
        this.props.toggleLoader(false);
    }

    async getShopList() {
        this.props.toggleLoader(true);
        const getShops = await axios.get(`${process.env.REACT_APP_BASE_URL}services/query/PCGetShops.php`);
        const shopData = getShops.data;
        let result = {};
        let ShopList = [];
        if (shopData.success === 1) {
            // Every shop, the signed-in shop included: it is a valid Recipient (a shop paying its own
            // expenses, e.g. wages). The old filter that removed it never matched (number vs string
            // ids), so shop users always had it; making the compare work on 2026-10-26 took it away.
            ShopList = shopData.data
                .map(shop => ({ value: shop.PC_SP_ID, label: shop.SP_NAME, type: "shop" }));
            result.data = shopData.data;
        }
        this.setState({ ShopList, AllShopData: shopData.data });
        this.props.toggleLoader(false);
        return result;
    }

    async getWarehouseList() {
        const getWarehouse = await axios.get(`${process.env.REACT_APP_BASE_URL}services/query/PCGetAllWarehouse.php`);
        const warehouseData = getWarehouse.data;
        let AllWareHouseList = [];
        if (warehouseData.success === 1) {
            AllWareHouseList = warehouseData.data.map(warehouse => ({
                value: warehouse.WAREHOUSE_ID,
                label: warehouse.WAREHOUSE_NAME,
                type: "warehouse"
            }));
        }
        this.setState({ AllWareHouseList });
        return AllWareHouseList;
    }

    async getAllPayaccount() {
        this.props.toggleLoader(true);
        const getAllPayAccounts = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/PCGetAllPayAccount.php`);
        const deviceData = getAllPayAccounts.data;
        let AccountList = [];
        if (deviceData.success === 1) {
            AccountList = deviceData.data.map(eachAccount => ({
                value: eachAccount.ACCOUNT_ID,
                label: eachAccount.ACCOUNT_TITLE,
                type: "account"
            }));
        }
        this.setState({ AccountList });
        this.props.toggleLoader(false);
        return AccountList;
    }

    // The SHOP LOCATION box's options: the user's own shops (every shop for an admin). ShopList
    // itself stays every shop — it also feeds the Recipient boxes.
    ownShopOptions(shopList = this.state.ShopList) {
        return limitOptions(shopList, ownLimit('shop', BASE_PATH), { type: "shop" });
    }

    buildGroupedOptions(ShopList, AccountList, AllWareHouseList) {
        return [
            { label: "Shop", options: ShopList },
            { label: "Account", options: AccountList },
            { label: "Warehouse", options: AllWareHouseList }
        ];
    }

    async hanldeAddShopPayment() {
        const [shopResult, AllWareHouseList, AccountList] = await Promise.all([
            this.getShopList(),
            this.getWarehouseList(),
            this.getAllPayaccount()
        ]);

        let isShopDisable = false, ShopPayment = "", ShopAccountPayDetails = [], PaymentWeek = "", shopPaymentDays = [];

        // Preselect the current week (and its day rows) for everyone — admins pick the shop
        // afterwards, which is what triggers getShopHistory() via handleChange.
        const weekResult = await this.getWeekNumberFormatted();
        shopPaymentDays = weekResult.shopPaymentDays;
        PaymentWeek = weekResult.WeekNumber;

        if (shopResult?.data) {
            // SHOP LOCATION lists only the user's own shop (selected and locked); admins pick from
            // every shop (user, 2026-11-02). The Recipient boxes keep every shop / account / warehouse.
            const ownShops = this.ownShopOptions(shopResult.data.map(shop => ({ value: shop.PC_SP_ID, label: shop.SP_NAME, type: "shop" })));
            if (ownLimit('shop', BASE_PATH) && ownShops.length === 1) {
                isShopDisable = true;
                ShopPayment = ownShops[0];
            }
            ShopAccountPayDetails = Array.from({ length: 10 }, () => ({
                ShopPay_Date: "",
                ShopPay_Acc: "",
                ShopPay_Type: "",
                ShopPay_Desc: "",
                ShopPay_Amnt: 0
            }));
        }

        const groupedOptions = this.buildGroupedOptions(this.state.ShopList, AccountList, AllWareHouseList);
        this.setState({ addShopPayment: true, isShopDisable, ShopPayment, ShopAccountPayDetails, PaymentWeek, shopPaymentDays, groupedOptions });
    }

    // Current week plus its day rows. The week comes from getWeekDetails() so it is the real
    // ISO week — the old inline Math.ceil((days + 1) / 7) count ran a week behind and
    // preselected a week the dropdown does not list.
    async getWeekNumberFormatted() {
        const WeekNumber = getWeekDetails().weekId;
        const WeekOut = await getWeekDayList(WeekNumber);
        const shopPaymentDays = WeekOut.map(WeekPerDay => ({ DateAndDay: WeekPerDay }));

        return { shopPaymentDays, WeekNumber };
    }

    // Fetch the account-payment "From" entries where this shop is the source (Case 1). Display-only:
    // these never affect CW Account / balance, they are shown for information with a total.
    async getShopFromEntries(SHOPID, SALEWEEK) {
        if (!SHOPID || !SALEWEEK) {
            this.setState({ FromEntries: [], FromEntriesTotal: 0 });
            return;
        }
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Finance/GetShopFromAccountEntries.php`, { SHOPID, SALEWEEK });
            if (res.data?.success === 1) {
                this.setState({ FromEntries: res.data.data || [], FromEntriesTotal: Number(res.data.total) || 0 });
            } else {
                this.setState({ FromEntries: [], FromEntriesTotal: 0 });
            }
        } catch (e) {
            this.setState({ FromEntries: [], FromEntriesTotal: 0 });
        }
    }

    /* Turn one GetShopHistory response into the on-screen figures.
     *
     * Shared by getShopHistory() and UpdateShopHistory(), which had identical copies of this
     * arithmetic. It is a PREVIEW only: pc_recompute_shop_payments() on the server decides the
     * stored numbers, and this mirrors it so the two agree before saving.
     *
     * Inter-shop transfer (F3): when this shop is an origin for the week, its own takings settle
     * at another shop, so they contribute nothing to Total here — CW Cash and CW Card still show
     * what it took. When it is a destination, the origins' full cash + card arrives as Other
     * Shop and is added. Calc type governs a shop's OWN takings only; transferred money settles
     * in full, which is why Other Shop is added outside netAmountMap.
     */
    applyShopHistory(shopData, SP_CAL_TYPE = "Cash") {
        const currentWeekSales = shopData.data;
        const previousWeekData = shopData.dataA;
        const preWeekData = Number(previousWeekData[0]?.SP_PREV_WEEK) || 0;
        const CWCashData = Number(currentWeekSales[0]?.CASH) || 0;
        const CWCardData = Number(currentWeekSales[0]?.CARD) || 0;
        const CWExpenseData = Number(currentWeekSales[0]?.SHOP_PAYMENT) || 0;
        const CWAccount = Number(shopData.cwAccount) || 0;

        const transfer = shopData.transfer || null;
        const isOrigin = !!(transfer && transfer.IS_ORIGIN);
        const otherShop = Number(transfer?.OTH_AMOUNT) || 0;

        // What counts toward this shop's own calc-type component.
        const effCash = isOrigin ? 0 : CWCashData;
        const effCard = isOrigin ? 0 : CWCardData;

        const netAmountMap = {
            "Card": preWeekData + effCard + CWAccount,
            "Both": preWeekData + effCard + CWAccount + effCash,
            "Cash": preWeekData + effCash + CWAccount,
        };

        // Use the calc type EFFECTIVE for this week (from the backend) so the preview matches
        // the backend's authoritative net even after the shop's calc type changes.
        const effCalType = shopData.calType || SP_CAL_TYPE;
        const totalAmountData = (netAmountMap[effCalType] ?? preWeekData + effCash + CWAccount) + otherShop;
        const NetAmount = totalAmountData - CWExpenseData;

        this.setState({
            SP_CAL_TYPE: effCalType,
            PrevWeek: money(preWeekData),
            CWCash: money(CWCashData),
            CWCard: money(CWCardData),
            OtherShop: money(otherShop),
            Transfer: transfer,
            TotalAmount: money(totalAmountData),
            NetAmount: money(NetAmount),
            Expense: money(CWExpenseData),
            CWAccount: money(CWAccount)
        });
    }

    async getShopHistory() {
        this.props.toggleLoader(true);
        const { ShopPayment, PaymentWeek, SP_CAL_TYPE } = this.state;
        this.getShopFromEntries(ShopPayment.value || "", PaymentWeek);
        const formData = {
            ShopPayment: ShopPayment.value || "",
            PaymentWeek
        };
        const getAllPayAccounts = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Finance/GetShopHistory.php`, formData);
        const shopData = getAllPayAccounts.data;

        if (shopData?.success === 1) {
            this.applyShopHistory(shopData, SP_CAL_TYPE);
        }
        this.props.toggleLoader(false);
    }

    async handleChange(fieldName, evt) {
        let value;
        switch (fieldName) {
            case 'ShopPayment': {
                value = evt;
                const findCalTypeOfShop = this.state.AllShopData.filter(x => x.PC_SP_ID === evt.value);
                if (findCalTypeOfShop.length > 0) {
                    this.setState({ SP_CAL_TYPE: findCalTypeOfShop[0].SP_CAL_TYPE });
                }
                const ShopAccountPayDetails = Array.from({ length: 10 }, () => ({
                    ShopPay_Date: "",
                    ShopPay_Acc: "",
                    ShopPay_Type: "",
                    ShopPay_Desc: "",
                    ShopPay_Amnt: 0
                }));
                this.setState({ ShopAccountPayDetails });
                break;
            }
            case 'PaymentWeek': {
                value = evt.target.value;
                const WeekOut = await getWeekDayList(value);
                const shopPaymentDays = WeekOut.map(WeekPerDay => ({ DateAndDay: WeekPerDay }));
                this.setState({ shopPaymentDays });
                break;
            }
            default:
                value = evt.target.value;
                break;
        }

        this.setState({ [fieldName]: value }, () => {
            if (fieldName === 'ShopPayment' || fieldName === 'PaymentWeek') {
                this.getShopHistory();
            }
        });
    }

    handleChangeShopPayment(index, fieldName, evt) {
        const ShopAccountPayDetails = [...this.state.ShopAccountPayDetails];
        const targetValue = fieldName === 'ShopPay_Acc' ? evt : evt.target.value;
        ShopAccountPayDetails[index] = { ...ShopAccountPayDetails[index], [fieldName]: targetValue };
        this.setState({ ShopAccountPayDetails }, () => {
            if (fieldName === 'ShopPay_Amnt') {
                const { ShopAccountPayDetails, TotalAmount } = this.state;
                const ExtraSum = sumMoney(ShopAccountPayDetails, "ShopPay_Amnt");
                const NetAmount = money(+TotalAmount - ExtraSum);
                this.setState({ Expense: money(ExtraSum), NetAmount });
            }
        });
    }

    validate() {
        if (!this.state.PaymentWeek) {
            Swal.fire('Error', 'Please Select Payment Week', 'error');
            return false;
        }
        if (!this.state.ShopPayment) {
            Swal.fire('Error', 'Please Select Shop', 'error');
            return false;
        }
        return true;
    }

    async handleSubmitShopPayment() {
        if (!this.validate()) return;
        this.props.toggleLoader(true);
        const formData = {
            SP_ID: this.state.SP_ID,
            ShopAccountPayDetails: JSON.stringify(this.state.ShopAccountPayDetails),
            PaymentWeek: this.state.PaymentWeek,
            ShopPayment: this.state.ShopPayment.value,
            SP_STATUS: "PENDING",
            PrevWeek: this.state.PrevWeek.toString(),
            CWCash: this.state.CWCash.toString(),
            CWAccount: this.state.CWAccount.toString(),
            Expense: this.state.Expense.toString(),
            NetAmount: (Number(this.state.NetAmount) - Number(this.state.PrevWeek)).toString(),
            OtherShop: this.state.OtherShop.toString(),
            CWCard: this.state.CWCard.toString(),
            TotalAmount: this.state.TotalAmount.toString(),
            shopPaymentDays: JSON.stringify(this.state.shopPaymentDays),
            CreatedBy: localStorage.getItem('PC_USR_NAME')
        };
        try {
            const getSaleResult = await axios.post(`${process.env.REACT_APP_BASE_URL}services/mutation/Finance/PCAddShopPayment.php`, formData);
            this.props.toggleLoader(false);
            if (getSaleResult.data.status === 201) {
                Swal.fire('Success', getSaleResult.data.message, 'success').then(res => {
                    if (res.isConfirmed) {
                        let { SP_ID } = this.state;
                        if (SP_ID === "") {
                            SP_ID = getSaleResult.data.extra[0].SP_ID;
                            // A new payment now has a link of its own.
                            recordOpened(this, BASE_PATH, getSaleResult.data.extra[0].PUBLIC_ID, { replace: true });
                        }
                        this.setState({ isEdit: true, isShopDisable: true, SP_ID });
                    } else {
                        this.handleExit();
                    }
                });
            } else {
                Swal.fire('Error', getSaleResult.data.message, 'error');
            }
        } catch (error) {
            this.props.toggleLoader(false);
            console.error('handleSubmitShopPayment error:', error);
        }
    }

    handleExit() {
        recordClosed(this, BASE_PATH);
        this.getShopPayment();
        this.setState({
            isEdit: false,
            addShopPayment: false,
            ShopAccountPayDetails: [],
            PaymentWeek: "",
            ShopPayment: "",
            PrevWeek: "0",
            CWCash: "0",
            CWAccount: "0",
            Expense: "0",
            NetAmount: "0",
            OtherShop: "0",
            Transfer: null,
            CWCard: "0",
            TotalAmount: "0",
            SP_ID: "",
            SP_STATUS: "PENDING",
            shopPaymentDays: [],
            groupedOptions: []
        });
    }

    async UpdateShopHistory(myData, SP_CAL_TYPE = "Cash") {
        this.props.toggleLoader(true);
        const formData = {
            ShopPayment: myData.SP_SHOPID,
            PaymentWeek: myData.SP_WEEK
        };
        this.getShopFromEntries(myData.SP_SHOPID, myData.SP_WEEK);
        const getAllPayAccounts = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Finance/GetShopHistory.php`, formData);
        const shopData = getAllPayAccounts.data;

        if (shopData?.success === 1) {
            this.applyShopHistory(shopData, SP_CAL_TYPE);
        }
        this.props.toggleLoader(false);
    }

    async handleUpdateSP(SP_ID) {
        try {
            const getShopPayByID = await axios.post(`${process.env.REACT_APP_BASE_URL}services/query/Finance/GetShopPaymentDataByID.php`, { SP_ID });
            if (getShopPayByID.status === 200 && getShopPayByID.data.data) {
                const myData = getShopPayByID.data.data[0];
                const shop_payment_days = getShopPayByID.data.shop_payment_days;

                const [AllShopData, AllWareHouseList, AccountList] = await Promise.all([
                    this.getShopList(),
                    this.getWarehouseList(),
                    this.getAllPayaccount()
                ]);

                const ShopData = AllShopData.data.find(x => +x.PC_SP_ID === +myData.SP_SHOPID);
                await this.UpdateShopHistory(myData, ShopData.SP_CAL_TYPE);

                const groupedOptions = this.buildGroupedOptions(this.state.ShopList, AccountList, AllWareHouseList);
                // The record's own 7 days, as Add does for the current week: they set the date boxes'
                // min / max (and the week label). Lost in the 2026-06-26 refactor, which dropped the
                // old restore from SP_DATE_RANGE (no longer written), so on edit every date could be
                // picked.
                const shopPaymentDays = (await getWeekDayList(myData.SP_WEEK)).map(day => ({ DateAndDay: day }));

                this.setState({
                    groupedOptions,
                    shopPaymentDays,
                    isEdit: true,
                    addShopPayment: true,
                    SP_ID: myData.SP_ID,
                    isShopDisable: true,
                    // Normalize each pay-to entry so ShopPay_Acc is always the option object
                    // {value,label,type}. The backend returns it as a scalar id on reload; if we
                    // kept it scalar, re-saving the week without re-picking the dropdown would send
                    // ShopPay_Acc->value/->type = null, wiping payTo_id and making the selected
                    // shop/account disappear on the next open.
                    ShopAccountPayDetails: shop_payment_days.map(d => ({
                        ...d,
                        // pay_date is a DATETIME ("2026-10-28 00:00:00"); the date box only takes
                        // "2026-10-28" and showed nothing. Worse, the blank box then saved as TODAY
                        // (the server reads an empty date as now), so every re-save overwrote the
                        // entry dates. Keep the stored day.
                        ShopPay_Date: d.ShopPay_Date ? String(d.ShopPay_Date).slice(0, 10) : '',
                        ShopPay_Acc: (d.ShopPay_Acc && typeof d.ShopPay_Acc === 'object')
                            ? d.ShopPay_Acc
                            : { value: d.ShopPay_Acc, label: d.ShopPay_AccName, type: d.ShopPay_Type }
                    })),
                    PaymentWeek: myData.SP_WEEK,
                    ShopPayment: { value: ShopData.PC_SP_ID, label: ShopData.SP_NAME, type: "shop" },
                    SP_STATUS: myData.SP_STATUS,
                    // Other Shop is NOT restored from the saved row here: applyShopHistory() has
                    // already derived it from the current transfer configuration, and that is the
                    // figure the server will store on the next save.
                });
            } else {
                Swal.fire('Not Found', 'Record not found', 'error');
            }
        } catch (error) {
            console.error('handleUpdateSP error:', error);
            Swal.fire('Error', 'Failed to load payment data', 'error');
        }
    }


    /* Bulk transition for the rows ticked in the list. Routed through the same shared

     * endpoint the per-record action uses (services/mutation/Approval/PCApproveFinance.php),

     * so the rules cannot drift between one record and fifty. Partial results are normal

     * and are reported per record by reportTransition(). */

    async handleBulkTransition(to, ids) {

        const confirm = await Swal.fire({

            title: `Update ${ids.length} shop payment${ids.length === 1 ? '' : 's'}?`,

            text: `They will be marked ${to.toLowerCase()}.`,

            icon: 'question',

            showCancelButton: true,

            confirmButtonText: 'Yes',

            cancelButtonText: 'No'

        });

        if (!confirm.isConfirmed) return;


        this.setState({ bulkBusy: true });

        const result = await runTransition(APPROVAL_MODULES.SHOP_PAYMENT, to, ids);

        this.setState({ bulkBusy: false, selectedIds: [] });

        if (result && result.approved > 0) this.getShopPayment();

    }

    async handleSendForApproval(Status) {
        /* Routed through the SAME shared endpoint the bulk bar uses, with a list of one.
         *
         * This replaced services/mutation/Finance/PCRequestShopPayment.php, which took the caller's identity
         * from the request body (PC_GRP_ID / PC_SHOP_ID) and gated on a hardcoded
         * "PC_GRP_ID == '2'". PCApproveFinance.php reads the role AND the owning shop from the
         * SIGNED token instead, so a caller can no longer claim to be an admin. */
        this.props.toggleLoader(true);
        /* The loader is hidden by the callback, BEFORE the dialog opens — it is a z-index
         * 10000 overlay and would otherwise sit on top of the dialog, leaving it visible but
         * unclickable and the page permanently blocked. */
        const result = await runTransition(APPROVAL_MODULES.SHOP_PAYMENT, Status, [this.state.SP_ID],
            () => this.props.toggleLoader(false));
        if (result && result.approved > 0) await this.reloadOpenRecord();
    }

    /* After an approval action, stay on the record and show its new status (user, 2026-11-05,
     * T1.76): reload it from the server. Falls back to the list when no record link is open. */
    async reloadOpenRecord() {
        if (!this.openPid) return this.handleExit();
        await this.openByPublicId(this.openPid);
        this.getShopPayment(); // the list behind the record shows the new status too
    }

    handleDelete(SP_ID) {
        Swal.fire({
            title: 'Do you want to delete shop payment?',
            icon: "question",
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then(async (result) => {
            if (result.isConfirmed) {
                this.props.toggleLoader(true);
                const res = await axios.post(`${process.env.REACT_APP_BASE_URL}services/delete/finance/DeleteShopPayment.php`, { SP_ID });
                if (res.data.success === 1) {
                    Swal.fire('Shop Payment Deleted!', '', 'success');
                    this.getShopPayment();
                }
                this.props.toggleLoader(false);
            }
        });
    }

    handleBlur(index, fieldName, evt) {
        const ShopAccountPayDetails = [...this.state.ShopAccountPayDetails];
        ShopAccountPayDetails[index] = { ...ShopAccountPayDetails[index], [fieldName]: money(evt.target.value) };
        this.setState({ ShopAccountPayDetails });
    }

    handleAddTableItem() {
        const ShopAccountPayDetails = [...this.state.ShopAccountPayDetails, {
            ShopPay_Date: "",
            ShopPay_Acc: "",
            ShopPay_Desc: "",
            ShopPay_Amnt: 0
        }];
        this.setState({ ShopAccountPayDetails });
    }

    handleRemoveTableItem(index) {
        const ShopAccountPayDetails = this.state.ShopAccountPayDetails.filter((_, i) => i !== index);
        this.setState({ ShopAccountPayDetails });
    }

    render() {
        const { addShopPayment, shopPaymentDays, ShopAccountPayDetails, SP_STATUS } = this.state;
        // Shop users (PC_GRP_ID 2) can only edit/send a PENDING payment for approval; once it is
        // REQUESTED/APPROVED they can no longer edit or delete it. Admins keep editing until
        // APPROVED. Mirrors WeeklySale / AccountPayments.
        const isShopUser = this.state.PC_GRP_ID === '2';
        const status = SP_STATUS;
        // Reopen is offered only to SUPER_ADMIN, and only on a record that is not already pending.
        const canReopen = isSuperAdmin() && status !== "PENDING";
        // Super Admin may save changes at ANY status and the record keeps it (T1.75, user; the
        // server applies the same rule via helper/Approval.php pc_approval_lock_check).
        const canEdit = isShopUser ? status === 'PENDING' : (status !== 'APPROVED' || isSuperAdmin());
        const isFormDisable = !canEdit;

        const weekLabel = shopPaymentDays.length > 0
            ? `${shopPaymentDays[0]['DateAndDay']} – ${shopPaymentDays[6]['DateAndDay']}`
            : '';

        const netAmt = Number(this.state.NetAmount);

        if (!addShopPayment) {
            return (
                <Container>
                    <BulkActionBar
                        rows={this.state.ShopPaymentsList.filter(r => this.state.selectedIds.includes(String(r.SP_ID)))}
                        statusField="SP_STATUS"
                        idField="SP_ID"
                        canApprove={this.state.canApprove}
                        busy={this.state.bulkBusy}
                        onAction={this.handleBulkTransition.bind(this)}
                        onClear={() => this.setState({ selectedIds: [] })}
                    />
                    <PhoneClinicGrid
                        selection={{
                            rowIds: this.state.ShopPaymentsList.map(r => String(r.SP_ID)),
                            // Only rows with a transition available to this user can be ticked.
                            selectableIds: this.state.ShopPaymentsList
                                .filter(r => approvalActions(r.SP_STATUS, this.state.canApprove).length > 0)
                                .map(r => String(r.SP_ID)),
                            selected: this.state.selectedIds,
                            onChange: (ids) => this.setState({ selectedIds: ids }),
                        }}
                        ref={this.gridRef}
                        title="SHOP PAYMENT"
                        buttonLabel="Shop Payment"
                        isEditable={true}
                        onClickButton={this.hanldeAddShopPayment.bind(this)}
                        getData={this.getShopPayment.bind(this)}
                        tableHeadData={[
                            { label: "WEEK", isHidden: false },
                            { label: "SHOP", isHidden: false },
                            { label: "WEEKLY EXPENSE", isHidden: false },
                            { label: "CREATED BY", isHidden: false },
                            { label: "STATUS", isHidden: false },
                        ]}
                    >
                        {this.state.ShopPaymentsList.map((service, idx) => {
                            return (
                                <TableRow
                                    key={idx}
                                    /* The grid reads this to know which record a checkbox selects. */
                                    data-pcid={String(service.SP_ID)}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    <TableCell align='left'>{formatDisplayWeek(service.SP_WEEK)}</TableCell>
                                    <TableCell align='left' style={{ cursor: 'pointer', color: "#416bc7" }}>{service.SHOP_NAME}</TableCell>
                                    <TableCell align='left'>{formatAmount(service.SP_EXP)}</TableCell>
                                    <TableCell align='left'>{service.SP_CREATED_BY || ""}</TableCell>
                                    <TableCell align='left'>
                                        <StatusChip label={service.SP_STATUS} />
                                    </TableCell>
                                    <TableCell align='center'>
                                        <RowActions actions={[
                                            { type: 'edit', onClick: this.openFromList.bind(this, service) },
                                            { type: 'delete', onClick: this.handleDelete.bind(this, service.SP_ID), show: (!isShopUser || service.SP_STATUS === "PENDING") },
                                        ]} />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </PhoneClinicGrid>
                </Container>
            );
        }

        return (
            <div style={{ background: '#f8f9ff', minHeight: '100vh', padding: 24 }}>
                <PageHeader title={this.state.isEdit ? 'Edit Shop Payment' : 'Add Shop Payment'}
                    mode={this.state.isEdit ? 'edit' : 'new'} style={{ margin: '0 0 16px' }} />
                {/* Filter bar */}
                <div className="pc-form-grid" style={{ ...CARD, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, padding: 20, marginBottom: 20 }}>
                    <div>
                        <label style={LABEL}>
                            SELECT WEEK
                            {weekLabel && (
                                <span style={{ textTransform: 'none', fontWeight: 400, marginLeft: 8, opacity: 0.9 }}>
                                    ({weekLabel})
                                </span>
                            )}
                        </label>
                        <WeekRangeSelect
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            /* Locked only on a saved record. A shop user's SHOP is fixed, but they pick the week
                               like an admin (the server refuses a second record for the same shop + week). */
                            disabled={this.state.isEdit}
                            value={this.state.PaymentWeek}
                            onChange={this.handleChange.bind(this, "PaymentWeek")}
                        />
                    </div>
                    <div>
                        <label style={LABEL}>SHOP LOCATION</label>
                        <Select menuPortalTarget={document.body}
                            menuPlacement="auto"
                            isDisabled={this.state.isShopDisable}
                            value={this.state.ShopPayment}
                            options={this.ownShopOptions()}
                            onChange={this.handleChange.bind(this, "ShopPayment")}
                        />
                    </div>
                </div>

                {/* Main content: table + sidebar. On a phone (pc-stack-mobile) the summary moves under the
                    entries instead of squeezing them to nothing beside its fixed 300px. 2026-10-28: the
                    same on any screen where the entries would get under ~720px (a 1024px laptop): the
                    row wraps and the summary goes underneath, full width, so the entry boxes keep their
                    values whole. */}
                <div className="pc-stack-mobile" style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
                    {/* Payment Entries Card */}
                    <div style={{ flex: '999 1 720px', minWidth: 0, ...CARD }}>
                        {/* Card header */}
                        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', background: 'rgba(239,244,255,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#a32f2f' }}>Payment Entries</h3>
                            {this.state.isEdit && !isFormDisable && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={this.handleAddTableItem.bind(this)}
                                    style={{ background: '#a32f2f', textTransform: 'none', fontWeight: 600, borderRadius: 8, boxShadow: 'none' }}
                                >
                                    Add Entry
                                </Button>
                            )}
                        </div>

                        {/* Table or placeholder */}
                        {!this.state.isEdit ? (
                            <div style={{ padding: '48px 24px', textAlign: 'center', color: '#94a3b8' }}>
                                <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
                                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No entries yet</div>
                                <div style={{ fontSize: 13 }}>Save the payment details first to start adding entries</div>
                            </div>
                        ) : (
                            <TableContainer sx={{ maxHeight: '60vh' }}>
                                <Table size="small" stickyHeader className="pc-card-table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ ...COL_HEAD, width: 48, textAlign: 'center' }}>S.No</TableCell>
                                            <TableCell style={COL_HEAD}>Date</TableCell>
                                            <TableCell style={COL_HEAD}>Recipient</TableCell>
                                            <TableCell style={COL_HEAD}>Description</TableCell>
                                            <TableCell style={{ ...COL_HEAD, width: 150 }}>Amount (£)</TableCell>
                                            {!isFormDisable && <TableCell style={{ ...COL_HEAD, width: 48 }} />}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ShopAccountPayDetails.map((service, idx) => {
                                            const accountValue = typeof service.ShopPay_Acc !== 'object'
                                                ? { value: service.ShopPay_Acc, label: service.ShopPay_AccName }
                                                : service.ShopPay_Acc;

                                            return (
                                                <TableRow
                                                    key={idx}
                                                    sx={{
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        '&:hover': { background: '#f8f9ff' },
                                                        '& .remove-btn': { opacity: 0, transition: 'opacity 0.15s' },
                                                        '&:hover .remove-btn': { opacity: 1 }
                                                    }}
                                                >
                                                    <TableCell style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 11, color: '#94a3b8', padding: '8px 16px' }}>
                                                        {String(idx + 1).padStart(2, '0')}
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        <Form.Control
                                                            type="date"
                                                            style={DATE_INPUT}
                                                            min={shopPaymentDays.length > 0 ? formatDate(shopPaymentDays[0]['DateAndDay']) : ''}
                                                            max={shopPaymentDays.length > 0 ? formatDate(shopPaymentDays[6]['DateAndDay']) : ''}
                                                            value={service.ShopPay_Date}
                                                            disabled={isFormDisable}
                                                            onChange={this.handleChangeShopPayment.bind(this, idx, 'ShopPay_Date')}
                                                        />
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px', minWidth: 180 }}>
                                                        <Select menuPortalTarget={document.body}
                                                            menuPlacement="auto"
                                                            value={accountValue}
                                                            isClearable
                                                            isDisabled={isFormDisable}
                                                            options={this.state.groupedOptions}
                                                            onChange={this.handleChangeShopPayment.bind(this, idx, 'ShopPay_Acc')}
                                                            styles={{
                                                                ...WRAP_SELECT,
                                                                control: (base) => ({ ...base, background: '#f1f3ff', border: 'none', borderRadius: 6, minHeight: 32, fontSize: 13 }),
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={1}
                                                            className={DESC_CLASS}
                                                            style={DESC_INPUT}
                                                            ref={(el) => fitDescription(el)}
                                                            title={service.ShopPay_Desc || undefined}
                                                            onKeyDown={keepOneLine}
                                                            value={service.ShopPay_Desc}
                                                            disabled={isFormDisable}
                                                            onChange={this.handleChangeShopPayment.bind(this, idx, 'ShopPay_Desc')}
                                                        />
                                                    </TableCell>
                                                    <TableCell style={{ padding: '8px 16px' }}>
                                                        <AmountInput
                                                            style={{ ...AMOUNT_INPUT, fontWeight: 700, color: '#a32f2f' }}
                                                            value={service.ShopPay_Amnt}
                                                            onBlur={this.handleBlur.bind(this, idx, 'ShopPay_Amnt')}
                                                            disabled={isFormDisable}
                                                            onChange={this.handleChangeShopPayment.bind(this, idx, 'ShopPay_Amnt')}
                                                        />
                                                    </TableCell>
                                                    {!isFormDisable && (
                                                        <TableCell style={{ padding: '8px 16px', textAlign: 'center' }}>
                                                            <button
                                                                className="remove-btn"
                                                                onClick={this.handleRemoveTableItem.bind(this, idx)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0, display: 'inline-flex', alignItems: 'center' }}
                                                            >
                                                                <Remove style={{ fontSize: 20 }} />
                                                            </button>
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Card footer */}
                        <div style={{ padding: '14px 20px', borderTop: '1px solid #f1f5f9', background: 'rgba(239,244,255,0.2)', display: 'flex', gap: 12 }}>
                            {canEdit && (
                                <Button
                                    variant="contained"
                                    onClick={this.handleSubmitShopPayment.bind(this)}
                                    style={{ background: '#a32f2f', textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '8px 28px', boxShadow: '0 4px 12px rgba(163,47,47,0.25)' }}
                                >
                                    {this.state.isEdit ? 'Update Payment' : 'Add Payment'}
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                onClick={this.handleExit.bind(this)}
                                style={{ textTransform: 'none', fontWeight: 600, borderRadius: 8, borderColor: '#e2e8f0', color: '#64748b', padding: '8px 28px' }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>

                    {/* Financial Summary Sidebar */}
                    <div className="pc-full-mobile" style={{ flex: '1 1 300px', minWidth: 300 }}>
                        {/* Summary card */}
                        <div style={{ ...CARD, padding: 20, marginBottom: 16 }}>
                            <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #f1f5f9', paddingBottom: 12, marginBottom: 16 }}>
                                Financial Summary
                            </div>

                            {/* Inter-shop transfer (F3). Legacy showed the destination's name and
                                NOTHING else for a transferring shop, so its takings could not be
                                read at all. Here the figures stay visible and only the settlement
                                contribution is zero, which is what the notice explains. */}
                            {this.state.Transfer?.IS_ORIGIN === true && (
                                <div style={{ padding: 12, borderRadius: 8, background: 'rgba(163,47,47,0.06)', borderLeft: '4px solid #a32f2f', marginBottom: 16 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#a32f2f', marginBottom: 4 }}>
                                        Settles at {this.state.Transfer.TO_SHOP_NAME}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
                                        This week's cash and card are collected by that shop, so they add
                                        nothing to the total below. The figures are shown for reference.
                                    </div>
                                </div>
                            )}

                            {this.state.Transfer?.FROM_SHOPS?.length > 0 && (
                                <div style={{ padding: 12, borderRadius: 8, background: '#f8f9ff', borderLeft: '4px solid #213145', marginBottom: 16 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0b1c30', marginBottom: 6 }}>
                                        Settling for {this.state.Transfer.FROM_SHOPS.length} other shop{this.state.Transfer.FROM_SHOPS.length === 1 ? '' : 's'}
                                    </div>
                                    {this.state.Transfer.FROM_SHOPS.map((f, i) => (
                                        <div key={f.SHOP_ID ?? i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#334155', padding: '2px 0' }}>
                                            <span>{f.SHOP_NAME}</span>
                                            <span style={{ fontWeight: 700 }}>£{formatAmount(Number(f.CASH) + Number(f.CARD))}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* PREV WEEK + OTHER SHOP */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                                <div style={{ padding: 12, borderRadius: 8, background: '#f8f9ff' }}>
                                    <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', opacity: 0.6, marginBottom: 4 }}>PREV WEEK</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#0b1c30' }}>£{formatAmount(this.state.PrevWeek)}</div>
                                </div>
                                <div style={{ padding: 12, borderRadius: 8, background: '#f8f9ff' }}>
                                    <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', opacity: 0.6, marginBottom: 4 }}>OTHER SHOP</div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: '#0b1c30' }}>£{formatAmount(this.state.OtherShop)}</div>
                                </div>
                            </div>

                            {/* Line items */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {[
                                    { label: 'CW Cash', value: this.state.CWCash },
                                    { label: 'CW Card', value: this.state.CWCard },
                                    { label: 'CW Account', value: this.state.CWAccount },
                                ].map(({ label, value }) => (
                                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 6px', borderRadius: 6 }}>
                                        <span style={{ fontSize: 14, color: '#64748b' }}>{label}</span>
                                        <span style={{ fontWeight: 700, fontSize: 14 }}>£{formatAmount(value)}</span>
                                    </div>
                                ))}

                                {/* TOTAL AMOUNT */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(163,47,47,0.06)', borderRadius: 8, borderLeft: '4px solid #a32f2f', marginTop: 8 }}>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#a32f2f' }}>TOTAL AMOUNT</span>
                                    <span style={{ fontSize: 18, fontWeight: 700, color: '#a32f2f' }}>£{formatAmount(this.state.TotalAmount)}</span>
                                </div>
                            </div>

                            {/* Expense + Net Amount */}
                            <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 16, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 6px' }}>
                                    <span style={{ fontSize: 14, color: '#64748b' }}>Expense</span>
                                    <span style={{ fontWeight: 700, fontSize: 14, color: '#ef4444' }}>£{formatAmount(this.state.Expense)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#213145', borderRadius: 8, color: 'white' }}>
                                    <span style={{ fontSize: 14, fontWeight: 700 }}>NET AMOUNT</span>
                                    <span style={{ fontSize: 18, fontWeight: 700 }}>
                                        {netAmt < 0 ? '-' : ''}£{formatAmount(Math.abs(netAmt))}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Account Payments From this Shop (Case 1) — display only, no balance effect */}
                        {this.state.FromEntries?.length > 0 && (
                            <div style={{ background: 'white', borderRadius: 12, padding: 20, marginTop: 16, boxShadow: '0 4px 20px -2px rgba(0,0,0,0.08)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                    <span style={{ fontWeight: 700, fontSize: 15, color: '#0b1c30' }}>Account Payments From this Shop</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Display only</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {this.state.FromEntries.map((f, i) => (
                                        <div key={f.ID ?? i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#f8f9ff', borderRadius: 6 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: 13, color: '#334155' }}>{f.ACCOUNT_TITLE || f.ACC_ID}</span>
                                                {f.TR_DET ? <span style={{ fontSize: 11, color: '#94a3b8' }}>{f.TR_DET}</span> : null}
                                            </div>
                                            <span style={{ fontWeight: 700, fontSize: 14 }}>£{formatAmount(f.TR_AMT)}</span>
                                        </div>
                                    ))}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'rgba(163,47,47,0.06)', borderRadius: 8, borderLeft: '4px solid #a32f2f', marginTop: 6 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#a32f2f' }}>TOTAL FROM</span>
                                        <span style={{ fontSize: 16, fontWeight: 700, color: '#a32f2f' }}>£{formatAmount(this.state.FromEntriesTotal)}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CTA card — send for approval. Shop users can only send for approval
                            (PENDING → REQUESTED); admins can also approve. */}
                        {this.state.isEdit && (isShopUser ? status === "PENDING" : status !== "APPROVED") && (
                            <div style={{ background: '#006591', borderRadius: 12, padding: 20, boxShadow: '0 4px 20px -2px rgba(0,0,0,0.08)' }}>
                                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginBottom: 16, lineHeight: 1.5 }}>
                                    Ready for processing? Ensure all entries are validated before submission.
                                </p>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<SendIcon />}
                                    onClick={this.handleSendForApproval.bind(this, SP_STATUS === "PENDING" ? "REQUESTED" : "APPROVED")}
                                    style={{ background: 'white', color: '#006591', textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '10px 0', boxShadow: 'none' }}
                                >
                                    {SP_STATUS === "PENDING" ? "Send for Approval" : "Approve"}
                                </Button>
                            </div>
                        )}
                        {/* Reopen, inside the form (2026-10-13). SUPER_ADMIN only, hardcoded to
                            match helper/Approval.php; the server re-checks APPROVE from the token. */}
                        {this.state.isEdit && canReopen && (
                            <div style={{ background: 'white', border: '1px solid #a32f2f', borderRadius: 12, padding: 20, marginTop: 16 }}>
                                <p style={{ color: '#a32f2f', fontSize: 14, marginBottom: 16, lineHeight: 1.5 }}>
                                    This record is {String(SP_STATUS).toLowerCase()}. As Super Admin you can save changes and it stays {String(SP_STATUS).toLowerCase()}; reopening sends it back to pending.
                                </p>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={this.handleSendForApproval.bind(this, "PENDING")}
                                    style={{ borderColor: '#a32f2f', color: '#a32f2f', textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '10px 0' }}
                                >
                                    Reopen to Edit
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter(ShopPayments);
